import type { RenewalBlockData } from "@/hooks/queries/org-admin-dashboard-types";

// Story 21.3 (gh-738, FR87) — the ONE place the renewal-blocked copy is written.
//
// The precedent is `contract-notice.ts:14-15`, and so is the licence: no mockup
// in `docs/screens` carries this state (25 and 26 were validated before the
// term-boundary work existed), so this story's field contract IS the source for
// the wording. Authored, not transcribed — and flagged for UX in the story's
// deferred list, because "authored by the implementer" is a fact the next
// reviewer should be told rather than left to infer.
//
// The copy lives in one module for the same reason the contract copy does: an
// org_admin meets this state on the dashboard (Screen 25) and on seat
// management (Screen 26), and two hand-written variants of one situation drift
// into two different explanations of it.

/**
 * The state, named without blame. A renewal being refused is CITS's request
 * failing a check, not the organization having done something wrong, and the
 * org_admin reading this did not make the request and has not seen it fail.
 *
 * EDITING THIS STRING BREAKS AN E2E LOCATOR, NOT JUST AN ASSERTION.
 * `frontend/e2e/org-admin-dashboard.spec.ts` re-types it and builds
 * `renewalNotice()` as `getByRole("alert").filter({ hasText: <this string> })`.
 * Change the wording here without changing it there and the locator matches
 * nothing: the `toHaveCount(1)` leg fails, but every `toHaveCount(0)` leg —
 * the absence assertions that prove the notice is GONE once seats are released
 * — starts passing against an empty locator, for the wrong reason. Edit both,
 * and keep an existence control beside any absence assertion.
 */
export const RENEWAL_BLOCK_TITLE = "Your next term is sized below your team";

/**
 * Singular and plural are TWO wordings rather than one template with a count
 * interpolated into it. `"1 seats"` is what the shared template produces, and
 * the count IS the content of this line — the `_PulseAggregate.singular` /
 * `plural` precedent in `get_org_admin_pulse/query.py` makes the same call for
 * the same reason.
 *
 * There are TWO counts in this sentence and they agree independently. Splitting
 * only on `seats_to_release` left the opening clause reading "open with 1 seats"
 * whenever CITS requested a one-seat term — the very defect the two-wordings
 * rule exists to prevent, reintroduced one clause to the left of it. The
 * opening clause is therefore its own singular/plural choice rather than a
 * third and fourth whole wording: `seats_to_release` selects the sentence
 * because the sentence's shape changes with it (person/people, a seat/seats),
 * while `next_term_seat_count` changes one noun and nothing else.
 *
 * Both wordings state the second remedy as well as the first. Releasing a seat
 * and agreeing a different seat count are equally real ways out of this, the PM
 * ruled on 2026-09-14 that only the organization can do the first, and a notice
 * that names one remedy reads as an instruction to let somebody go. It is a
 * choice, and it says so.
 *
 * The verb is *release*, which is the platform's own word for taking a person
 * off a seat and is what the roster's own control says. `remove`, `terminate`,
 * `dismiss` and `end their access` are all deliberately absent: the seat is
 * what is being released, the person keeps their account, and three of those
 * four words describe something that is simply not happening.
 *
 * Nothing here says CITS can release a seat, because CITS cannot — and a notice
 * that implied otherwise would send the org_admin to wait for a phone call that
 * resolves nothing.
 *
 * EDITING THIS WORDING TOUCHES A SECOND FILE. `frontend/e2e/org-admin-dashboard
 * .spec.ts` re-types both sentences — Playwright runs against the built app and
 * cannot import this module — and asserts them with `toContainText`. That spec
 * cites this function by symbol, but the citation only helps someone reading
 * the spec; this note is the other half of it, for whoever gets here first.
 * The matched substring is the whole re-typed sentence, so a plural-branch
 * edit breaks that assertion immediately rather than drifting quietly — the
 * cost is a red spec in a suite unrelated to the edit, not a false green.
 *
 * The SINGULAR branch is the one with no net under it. The spec's blocked leg
 * always renews at more than one seat (its own comment says so), so
 * `nextSeats === 1 ? "one seat"` is re-typed there and never evaluated. An
 * edit to that branch alone is invisible to every gate. The E2E workflow is
 * `workflow_dispatch` only and is not a merge gate, so neither half is caught
 * by CI regardless.
 */
export function renewalBlockBody(block: RenewalBlockData): string {
  const { seats_to_release: seats, next_term_seat_count: nextSeats } = block;
  const opening = nextSeats === 1 ? "one seat" : `${nextSeats} seats`;
  if (seats === 1) {
    return (
      `Your next term is set to open with ${opening}, and you currently have ` +
      `one more person than that holding a seat. Release one seat from the list below, ` +
      `or ask CITS to renew at a higher seat count.`
    );
  }
  return (
    `Your next term is set to open with ${opening}, and you currently have ` +
    `${seats} more people than that holding seats. Release ${seats} seats from the list ` +
    `below, or ask CITS to renew at a higher seat count.`
  );
}

export interface RenewalBlockNotice {
  title: string;
  body: string;
}

/**
 * The banner content for a renewal-blocked organization, or null when there is
 * nothing to say.
 *
 * `undefined` takes the same branch as `null` on purpose. It arrives from two
 * places that both mean "no notice": a dashboard query that has not resolved
 * yet, and an API deployed before this key existed (Rule 06 skew). A screen
 * must not render a commercial warning off a state it has not read, and there
 * is no enforcement riding on this — the server-side ORG-022 refusal is the
 * enforcement, and it does not consult the client at all.
 */
export function renewalBlockNotice(
  block: RenewalBlockData | null | undefined,
): RenewalBlockNotice | null {
  if (!block) return null;
  // Both figures are typed required, so TypeScript cannot catch an API deployed
  // before this key existed (Rule 06 skew) or a truncated envelope — and the
  // body interpolates both directly, so an absent one renders the literal
  // "undefined seats" to an org_admin. A `<` comparison does not stand in for
  // this check: `undefined < 1` is FALSE, so the guard below used to let exactly
  // the broken payload through. Same rule as above — say nothing rather than
  // state a figure that was never read.
  if (
    typeof block.seats_to_release !== "number" ||
    typeof block.next_term_seat_count !== "number"
  ) {
    return null;
  }
  if (block.seats_to_release < 1) return null;
  return { title: RENEWAL_BLOCK_TITLE, body: renewalBlockBody(block) };
}
